/* eslint-disable react/no-unknown-property */
import React, { useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Environment, Grid, Text } from "@react-three/drei";
import * as THREE from "three";

const Bulb = ({ position, intensity }) => {
  const glowRef = useRef();
  useFrame(({ clock }) => {
    if (glowRef.current) {
      const flick = 0.9 + Math.sin(clock.getElapsedTime() * 12) * 0.05;
      glowRef.current.intensity = intensity * flick;
    }
  });
  const color = new THREE.Color().setHSL(0.13, 1, 0.5 + intensity * 0.15);
  return (
    <group position={position}>
      {/* Bulb glass */}
      <mesh>
        <sphereGeometry args={[0.28, 24, 24]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={intensity * 1.5}
          transparent
          opacity={0.85}
          metalness={0.1}
          roughness={0.2}
        />
      </mesh>
      {/* Filament */}
      <mesh>
        <torusGeometry args={[0.08, 0.01, 8, 16]} />
        <meshStandardMaterial color="#fef08a" emissive="#fde047" emissiveIntensity={intensity * 4} />
      </mesh>
      {/* Base */}
      <mesh position={[0, -0.3, 0]}>
        <cylinderGeometry args={[0.12, 0.12, 0.12, 16]} />
        <meshStandardMaterial color="#78716c" metalness={0.8} roughness={0.3} />
      </mesh>
      {/* Halo */}
      <pointLight ref={glowRef} intensity={intensity * 2.5} distance={3} decay={2} color="#fde047" />
    </group>
  );
};

const Battery = ({ position, voltage }) => (
  <group position={position}>
    <mesh>
      <boxGeometry args={[0.9, 0.5, 0.4]} />
      <meshStandardMaterial color="#0f172a" metalness={0.7} roughness={0.3} />
    </mesh>
    {/* Positive terminal */}
    <mesh position={[0.5, 0, 0]}>
      <boxGeometry args={[0.12, 0.15, 0.15]} />
      <meshStandardMaterial color="#22c55e" emissive="#16a34a" emissiveIntensity={0.4} />
    </mesh>
    {/* Negative terminal */}
    <mesh position={[-0.5, 0, 0]}>
      <boxGeometry args={[0.12, 0.15, 0.15]} />
      <meshStandardMaterial color="#ef4444" emissive="#b91c1c" emissiveIntensity={0.4} />
    </mesh>
    <Text position={[0, 0.4, 0]} fontSize={0.14} color="#22d3ee">
      {`${voltage.toFixed(1)} V`}
    </Text>
  </group>
);

const Wire = ({ from, to, glow = 0 }) => {
  const [ax, ay, az] = from;
  const [bx, by, bz] = to;
  const mid = [(ax + bx) / 2, (ay + by) / 2, (az + bz) / 2];
  const len = Math.hypot(bx - ax, by - ay, bz - az);
  const dir = new THREE.Vector3(bx - ax, by - ay, bz - az).normalize();
  const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
  const rot = new THREE.Euler().setFromQuaternion(q);
  return (
    <mesh position={mid} rotation={rot}>
      <cylinderGeometry args={[0.04, 0.04, len, 12]} />
      <meshStandardMaterial
        color={glow > 0 ? "#22d3ee" : "#475569"}
        emissive={glow > 0 ? "#0891b2" : "#000"}
        emissiveIntensity={glow}
        metalness={0.6}
        roughness={0.3}
      />
    </mesh>
  );
};

const Circuit3D = ({ params }) => {
  const V = +params.V || 6;
  const config = params.config || "series"; // series | parallel
  const nBulbs = 2;
  const R_each = 3;
  const R_total = config === "series" ? R_each * nBulbs : R_each / nBulbs;
  const current = V / R_total;
  const intensityPerBulb = config === "series" ? current * (R_each / R_total) * 0.7 : Math.min(current * 0.4, 1);
  const clamp = (n) => Math.max(0, Math.min(1, n));

  // Layout positions
  const bulbY = 1.2;
  const bat = [0, 0.4, 0];
  const b1 = config === "series" ? [-1.5, bulbY, 0] : [-1.2, bulbY, -0.6];
  const b2 = config === "series" ? [1.5, bulbY, 0] : [1.2, bulbY, -0.6];

  const wires =
    config === "series"
      ? [
          { from: [0.45, 0.4, 0], to: [b2[0], b2[1] - 0.35, 0] },
          { from: [b2[0], b2[1] - 0.35, 0], to: [b2[0], 2.2, 0] },
          { from: [b2[0], 2.2, 0], to: [b1[0], 2.2, 0] },
          { from: [b1[0], 2.2, 0], to: [b1[0], b1[1] - 0.35, 0] },
          { from: [b1[0], b1[1] - 0.35, 0], to: [-0.45, 0.4, 0] },
        ]
      : [
          { from: [-0.45, 0.4, 0], to: [b1[0], b1[1] - 0.35, 0] },
          { from: [b1[0], b1[1] - 0.35, 0], to: [b1[0], 2.2, 0] },
          { from: [b1[0], 2.2, 0], to: [b2[0], 2.2, 0] },
          { from: [b2[0], 2.2, 0], to: [b2[0], b2[1] - 0.35, 0] },
          { from: [b2[0], b2[1] - 0.35, 0], to: [0.45, 0.4, 0] },
        ];

  return (
    <Canvas shadows camera={{ position: [0, 3, 5], fov: 45 }} dpr={[1, 2]}>
      <color attach="background" args={["#020617"]} />
      <ambientLight intensity={0.3} />
      <directionalLight position={[5, 5, 3]} intensity={0.8} />

      <Battery position={bat} voltage={V} />
      <Bulb position={b1} intensity={clamp(intensityPerBulb)} />
      <Bulb position={b2} intensity={clamp(intensityPerBulb)} />

      {wires.map((w, i) => (
        <Wire key={i} from={w.from} to={w.to} glow={clamp(current / 3)} />
      ))}

      <Grid args={[8, 8]} cellColor="#1e293b" sectionColor="#0891b2" position={[0, 0, 0]} fadeDistance={10} />

      <Text position={[-2.5, 3, 0]} fontSize={0.16} color="#22d3ee" anchorX="left">
        {`Config: ${config}`}
      </Text>
      <Text position={[-2.5, 2.7, 0]} fontSize={0.16} color="#22d3ee" anchorX="left">
        {`I = ${current.toFixed(2)} A · R = ${R_total.toFixed(1)} Ω`}
      </Text>

      <Environment preset="night" />
      <OrbitControls enablePan={false} minDistance={3} maxDistance={10} />
    </Canvas>
  );
};

export default Circuit3D;
